"use client";

import { createContext, type JSX, useContext, useRef } from "react";
import { type StoreApi, useStore } from "zustand";
import {
  createExtendDietCartStore,
  type ExtendDietCartStore,
} from "../store/extend-diet-cart-store";

export const ExtendDietCartStoreContext =
  createContext<StoreApi<ExtendDietCartStore> | null>(null);

// Dummy store for fallback when context doesn't exist
const dummyStore = createExtendDietCartStore({
  storageKey: "__dummy_extend_diet_cart__",
});

type Props = {
  children: JSX.Element;
};

export const ExtendDietCartProvider = ({ children }: Props) => {
  const storeRef = useRef<StoreApi<ExtendDietCartStore>>(undefined);
  if (!storeRef.current) {
    storeRef.current = createExtendDietCartStore({
      storageKey: "extend-diet-cart",
    });
  }

  return (
    <ExtendDietCartStoreContext.Provider value={storeRef.current}>
      {children}
    </ExtendDietCartStoreContext.Provider>
  );
};

export const useExtendDietCartStore = <T,>(
  selector: (store: ExtendDietCartStore) => T
): T => {
  const cartStoreContext = useContext(ExtendDietCartStoreContext);

  if (!cartStoreContext) {
    throw new Error(
      "useExtendDietCartStore must be use within ExtendDietCartProvider"
    );
  }

  return useStore(cartStoreContext, selector);
};

export const useExtendDietCartStoreSafe = <T,>(
  selector: (store: ExtendDietCartStore) => T
): T | null => {
  const cartStoreContext = useContext(ExtendDietCartStoreContext);
  const store = cartStoreContext || dummyStore;
  const result = useStore(store, selector);

  // Return null if context doesn't exist, otherwise return the result
  return cartStoreContext ? result : null;
};
